import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, Pause, Play, Pencil, Heart, Share2 } from "lucide-react";
import { colorForName, getInitials } from "@/components/card/SignersBubbles";
import { useToast } from "@/hooks/use-toast";
import SignerProfileSheet from "@/components/card/SignerProfileSheet";
import type { Tables } from "@/integrations/supabase/types";

interface StoriesViewerProps {
  templateUrl: string;
  title: string;
  messages: Tables<"messages">[];
  contributions: Tables<"contributions">[];
  onClose: () => void;
  startIndex?: number;
  onAction?: (action: "sign" | "chipIn") => void;
  onStorySeen?: (contributorKey: string) => void;
  musicId?: string | null;
}

const STORY_DURATION = 5000; // 5s per story

// Fancy gradient backgrounds for stories
const STORY_BACKGROUNDS = [
  "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
  "linear-gradient(135deg, #141e30 0%, #243b55 100%)",
  "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
  "linear-gradient(135deg, #2d1b69 0%, #11998e 100%)",
  "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)",
  "linear-gradient(135deg, #1f1c2c 0%, #928dab 100%)",
  "linear-gradient(135deg, #360033 0%, #0b8793 100%)",
  "linear-gradient(135deg, #4a0e4e 0%, #531853 33%, #3a1c71 66%, #0e3d6b 100%)",
  "linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 40%, #e94560 100%)",
  "linear-gradient(135deg, #1b0a3c 0%, #3c1a6e 40%, #e84393 100%)",
  "linear-gradient(135deg, #0a192f 0%, #172a45 50%, #1eb980 100%)",
  "linear-gradient(135deg, #200122 0%, #6f0000 100%)",
  "linear-gradient(135deg, #000428 0%, #004e92 100%)",
  "linear-gradient(135deg, #2c003e 0%, #512b58 50%, #e26a6a 100%)",
  "linear-gradient(135deg, #1d2b64 0%, #f8cdda 100%)",
  "linear-gradient(135deg, #0c0c1d 0%, #1a1a40 40%, #5f0f40 100%)",
];

function getStoryBackground(index: number, name?: string) {
  let hash = index * 7;
  if (name) {
    for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  }
  return STORY_BACKGROUNDS[Math.abs(hash) % STORY_BACKGROUNDS.length];
}

export default function StoriesViewer({
  templateUrl,
  title,
  messages,
  contributions,
  onClose,
  startIndex = 0,
  onAction,
  onStorySeen,
  musicId,
}: StoriesViewerProps) {
  const { toast } = useToast();
  // Story 0 = intro splash, stories 1..N = signers, story N+1 = CTA
  const hasCTA = !!onAction;
  const totalStories = 1 + messages.length + (hasCTA ? 1 : 0);
  const ctaIndex = 1 + messages.length;
  const [current, setCurrent] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [musicPlaying, setMusicPlaying] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const elapsedRef = useRef(0);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  // Background music
  useEffect(() => {
    let cancelled = false;
    const startMusic = async () => {
      let url = "https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3";
      if (musicId) {
        const { data } = await supabase
          .from("story_music")
          .select("audio_url")
          .eq("id", musicId)
          .maybeSingle();
        if (data?.audio_url) {
          // Spotify/SoundCloud can't autoplay as bg music — fall back to default
          const isStreaming =
            /open\.spotify\.com/.test(data.audio_url) ||
            data.audio_url.includes("soundcloud.com");
          if (!isStreaming) url = data.audio_url;
        }
      }
      if (cancelled) return;

      const audio = new Audio(url);
      audio.loop = false;
      audio.volume = 0.3;
      musicRef.current = audio;
      audio.play().catch(() => {
        setMusicPlaying(false);
      });
    };
    startMusic();
    return () => {
      cancelled = true;
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.src = "";
        musicRef.current = null;
      }
    };
  }, [musicId]);

  // Sync music play/pause with story pause and music toggle
  useEffect(() => {
    if (!musicRef.current) return;
    if (paused || !musicPlaying) {
      musicRef.current.pause();
    } else {
      musicRef.current.play().catch(() => {});
    }
  }, [paused, musicPlaying]);

  const goNext = useCallback(() => {
    if (current < totalStories - 1) {
      setCurrent((c) => c + 1);
      setProgress(0);
      elapsedRef.current = 0;
    } else {
      onClose();
    }
  }, [current, totalStories, onClose]);

  const goPrev = useCallback(() => {
    if (current > 0) {
      setCurrent((c) => c - 1);
      setProgress(0);
      elapsedRef.current = 0;
    }
  }, [current]);

  // Auto-advance timer
  useEffect(() => {
    if (paused) return;

    const currentMessage = current > 0 ? messages[current - 1] : null;
    const hasVideo = currentMessage?.video_url;
    const hasAudio = currentMessage?.audio_url;
    // Don't auto-advance on video/audio stories or CTA
    if (hasVideo || hasAudio) return;
    if (hasCTA && current === ctaIndex) return;


    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = elapsedRef.current + (now - startTimeRef.current);
      const pct = Math.min(elapsed / STORY_DURATION, 1);
      setProgress(pct);
      if (pct >= 1) {
        goNext();
      } else {
        timerRef.current = requestAnimationFrame(animate);
      }
    };

    timerRef.current = requestAnimationFrame(animate);
    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
      elapsedRef.current += Date.now() - startTimeRef.current;
    };
  }, [current, paused, goNext, messages]);

  // Reset progress on story change
  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
  }, [current]);

  // Mark signer story as seen
  useEffect(() => {
    if (current > 0 && onStorySeen) {
      const msg = messages[current - 1];
      if (msg) {
        const key = msg.contributor_email || msg.contributor_name;
        onStorySeen(key);
      }
    }
  }, [current, messages, onStorySeen]);

  const handleTap = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-controls]")) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width * 0.3) goPrev();
    else goNext();
  };

  const currentMessage = current > 0 ? messages[current - 1] : null;
  const currentContribution = currentMessage
    ? contributions.find(
        (c) => c.contributor_email === currentMessage.contributor_email && c.status === "completed"
      )
    : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center"
      onClick={handleTap}
    >
      {/* Mobile: full screen / Desktop: phone-sized container */}
      <div className="relative w-full h-full sm:w-[390px] sm:h-[85vh] sm:max-h-[844px] sm:rounded-2xl sm:overflow-hidden bg-black">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 px-3 pt-3" data-controls>
          {Array.from({ length: totalStories }).map((_, i) => (
            <div key={i} className="flex-1 h-[3px] rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-none"
                style={{
                  width: `${i < current ? 100 : i === current ? progress * 100 : 0}%`,
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-0 right-0 z-10 flex items-center justify-between px-4" data-controls>
          <div className="flex items-center gap-3">
            {current === 0 ? (
              <>
                {templateUrl ? (
                  <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/30">
                    <img src={templateUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white text-sm">
                    🎉
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">{title}</p>
                  <p className="text-white/50 text-[10px]">Stories</p>
                </div>
              </>
            ) : currentMessage ? (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaused(true);
                    setShowProfile(true);
                  }}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ring-2 ring-white/30 hover:ring-white/60 transition-all"
                >
                  <div className={`w-full h-full rounded-full flex items-center justify-center ${colorForName(currentMessage.contributor_name)}`}>
                    {currentMessage.photo_url ? (
                      <img src={currentMessage.photo_url} alt="" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      getInitials(currentMessage.contributor_name)
                    )}
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaused(true);
                    setShowProfile(true);
                  }}
                >
                  <p className="text-white text-sm font-semibold leading-tight text-left">{currentMessage.contributor_name}</p>
                  {currentContribution && (
                    <p className="text-emerald-400 text-[10px] font-medium text-left">
                      Chipped in ${Number(currentContribution.amount).toFixed(0)}
                    </p>
                  )}
                </button>
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMusicPlaying((m) => !m);
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
              title={musicPlaying ? "Pause music" : "Play music"}
            >
              {musicPlaying ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPaused((p) => !p);
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
            >
              {paused ? <Play size={16} /> : <Pause size={16} />}
            </button>
            {(currentMessage?.video_url || currentMessage?.audio_url) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMuted((m) => !m);
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            )}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const shareUrl = window.location.href;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: title || "Check this out!", url: shareUrl });
                  } else {
                    await navigator.clipboard.writeText(shareUrl);
                    toast({ title: "Link copied!", description: "Share it with friends" });
                  }
                } catch {
                  // Fallback: use a temporary input
                  const input = document.createElement("input");
                  input.value = shareUrl;
                  document.body.appendChild(input);
                  input.select();
                  document.execCommand("copy");
                  document.body.removeChild(input);
                  toast({ title: "Link copied!", description: "Share it with friends" });
                }
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
              title="Share"
            >
              <Share2 size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Story content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="w-full h-full flex items-center justify-center"
            style={{
              background: current === 0
                ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
                : getStoryBackground(current, currentMessage?.contributor_name),
            }}
          >
            {current === 0 ? (
              /* Intro splash story */
              <div className="w-full h-full flex flex-col items-center justify-center px-10 py-24 text-center gap-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
                  className="text-6xl"
                >
                  💌
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-3"
                >
                  <h2 className="text-2xl font-bold text-white leading-snug">
                    See what everyone<br />shared with you
                  </h2>
                  <p className="text-white/50 text-sm max-w-[260px] mx-auto leading-relaxed">
                    {messages.length} {messages.length === 1 ? "person" : "people"} left heartfelt messages just for you ✨
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex -space-x-2 mt-2"
                >
                  {messages.slice(0, 5).map((m, i) => (
                    <div
                      key={m.id}
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium ring-2 ring-black/30 ${colorForName(m.contributor_name)}`}
                      style={{ zIndex: 5 - i }}
                    >
                      {getInitials(m.contributor_name)}
                    </div>
                  ))}
                  {messages.length > 5 && (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-[10px] font-medium bg-white/20 text-white ring-2 ring-black/30">
                      +{messages.length - 5}
                    </div>
                  )}
                </motion.div>
              </div>
            ) : hasCTA && current === ctaIndex ? (
              /* CTA story — sign / chip in */
              <div className="w-full h-full flex flex-col items-center justify-center px-8 py-24 gap-6">
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center">
                  <Heart size={40} className="text-primary" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-2xl font-bold text-white">Add your touch!</h3>
                  <p className="text-white/60 text-sm max-w-xs">
                    Sign the card and leave a message for {title}
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full max-w-[260px]" data-controls>
                  <button
                    onClick={(e) => { e.stopPropagation(); onAction?.("sign"); }}
                    className="w-full h-12 rounded-full bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                  >
                    <Pencil size={16} />
                    Sign the Card
                  </button>
                  {onAction && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onAction("chipIn"); }}
                      className="w-full h-12 rounded-full border border-white/30 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                    >
                      💰 Chip in for Gift
                    </button>
                  )}
                </div>
              </div>
            ) : currentMessage ? (
              /* Signer story */
              <div className="w-full h-full flex flex-col items-center justify-center px-8 py-24">
                {/* Video story */}
                {currentMessage.video_url && (
                  <div className="w-full max-w-[380px] rounded-2xl overflow-hidden bg-black shadow-2xl">
                    <video
                      key={currentMessage.id}
                      src={currentMessage.video_url}
                      autoPlay
                      loop
                      muted={muted}
                      playsInline
                      className="w-full max-h-[60vh] object-contain"
                    />
                  </div>
                )}

                {/* GIF story */}
                {currentMessage.gif_url && !currentMessage.video_url && (
                  <div className="w-full max-w-[380px] rounded-2xl overflow-hidden shadow-2xl">
                    <img src={currentMessage.gif_url} alt="GIF" className="w-full object-contain max-h-[50vh]" />
                  </div>
                )}

                {/* Photo story */}
                {currentMessage.photo_url && !currentMessage.video_url && !currentMessage.gif_url && (
                  <div className="w-full max-w-[380px] rounded-2xl overflow-hidden shadow-2xl">
                    <img src={currentMessage.photo_url} alt="Photo" className="w-full object-cover max-h-[50vh]" />
                  </div>
                )}

                {/* Audio story */}
                {currentMessage.audio_url && !currentMessage.video_url && (
                  <div className="flex flex-col items-center gap-6">
                    <div className={`w-28 h-28 rounded-full flex items-center justify-center ${colorForName(currentMessage.contributor_name)}`}>
                      <Volume2 size={36} className="opacity-60 animate-pulse" />
                    </div>
                    <audio
                      key={currentMessage.id}
                      src={currentMessage.audio_url}
                      autoPlay
                      muted={muted}
                      className="w-64"
                      controls
                    />
                  </div>
                )}

                {/* Message text */}
                {currentMessage.message_text && (
                  <div className="mt-8 max-w-sm text-center">
                    <p className="text-white text-lg leading-relaxed font-light italic">
                      "{currentMessage.message_text}"
                    </p>
                  </div>
                )}

                {/* Signer name label */}
                <div className="mt-4 text-center">
                  <p className="text-white/80 text-sm font-semibold">
                    — {currentMessage.contributor_name}
                  </p>
                </div>

                {/* Initials-only story (no media) */}
                {!currentMessage.video_url && !currentMessage.gif_url && !currentMessage.photo_url && !currentMessage.audio_url && !currentMessage.message_text && (
                  <div className={`w-28 h-28 rounded-full flex items-center justify-center text-3xl font-medium ${colorForName(currentMessage.contributor_name)}`}>
                    {getInitials(currentMessage.contributor_name)}
                  </div>
                )}
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>

        {/* Tap zones hint (subtle) */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none">
          <span className="text-[10px] text-white/30 font-medium tracking-wide">
            {current}/{totalStories - 1} · Tap sides to navigate
          </span>
        </div>
      </div>

      {/* Signer Profile Sheet */}
      <AnimatePresence>
        {showProfile && currentMessage && (
          <SignerProfileSheet
            contributorName={currentMessage.contributor_name}
            contributorEmail={currentMessage.contributor_email}
            contribution={currentContribution}
            onClose={() => {
              setShowProfile(false);
              setPaused(false);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
